/**
 * @file SectionTargetField.tsx
 * @description «Куда ведёт» кнопка, плитка, ссылка шапки или объявление
 * (phase19.16 Р2; uiux-design-r2 §4; `sectionTargetSchema` shared): вид
 * цели [Страница / Категория / Рубрика блога / Документ / Раздел / Адрес] и
 * выбор из списка магазина (те же списки, что у пунктов меню —
 * `useMenuTargets`). Ссылка хранится без FK: удалённая цель на сайте
 * скрывается (SB16). Адрес — путь сайта, https://, tel: или mailto:.
 *
 * Партия «Юридические документы» (uiux-legal-steps-b-c §8.1): цель «Документ»
 * выбирается тем же полем, что у пункта меню (`LegalTargetField`: группы и
 * документы, «+ Создать» автоматической группы сертификатов).
 */

import { isSafeSectionUrl, SITE_SYSTEM_ROUTES, type SectionTarget, type SiteSystemRoute } from "@chaiberi/shared";
import { useEffect } from "react";

import { Combobox } from "../../../ui/combobox";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { SelectField } from "../../../ui/SelectField";
import { Segmented } from "../../../site-view/Segmented";
import { LegalTargetField } from "../../../site-view/legal/LegalTargetField";
import { useMenuTargets } from "../../../../hooks/site-view/useMenuTargets";
import type { MenuTargetList } from "../../../../lib/site-view/menu-link-types";
import { SYSTEM_ROUTE_LABELS } from "../../../../lib/site-view/menu-link-types";

type TargetType = SectionTarget["type"];

/** Виды цели: подпись и список для выбора (OCP — новый вид = запись). */
const TARGET_TYPES: ReadonlyArray<{ type: TargetType; label: string; list?: MenuTargetList }> = [
  { type: "system", label: "Раздел сайта" },
  { type: "page", label: "Страница", list: "pages" },
  { type: "category", label: "Категория каталога", list: "categories" },
  { type: "content-category", label: "Рубрика блога", list: "contentCategories" },
  { type: "legal", label: "Документ или группа документов", list: "legalDocuments" },
  { type: "url", label: "Адрес или телефон" },
];

interface Props {
  label?: string;
  value: SectionTarget | null;
  onChange: (target: SectionTarget | null) => void;
  /** Можно «Никуда» (плитка без ссылки, объявление просто текстом). */
  optional?: boolean;
  error?: string | null;
}

/** Цель по умолчанию для вида. */
function emptyTarget(type: TargetType): SectionTarget {
  if (type === "system") return { type, route: "CATALOG" };
  if (type === "url") return { type, url: "" };
  return { type, id: "" };
}

export function SectionTargetField({ label = "Куда ведёт", value, onChange, optional = false, error }: Props) {
  const targets = useMenuTargets();
  const meta = TARGET_TYPES.find((t) => t.type === value?.type);
  const list = meta?.list;
  const { ensure } = targets;
  // Документы грузит своё поле (`useLegalTargets`).
  useEffect(() => {
    if (list && list !== "legalDocuments") ensure(list);
  }, [list, ensure]);

  const typeOptions = [...(optional ? [{ value: "none", label: "Никуда" }] : []), ...TARGET_TYPES.map((t) => ({ value: t.type, label: t.label }))];
  const urlInvalid = value?.type === "url" && value.url !== "" && !isSafeSectionUrl(value.url);

  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      <SelectField
        ariaLabel={label}
        value={value?.type ?? "none"}
        options={typeOptions}
        onChange={(v) => onChange(v === "none" ? null : emptyTarget(v as TargetType))}
      />
      {value?.type === "system" ? (
        <Segmented
          aria-label="Раздел сайта"
          options={SITE_SYSTEM_ROUTES.map((r) => ({ value: r, label: SYSTEM_ROUTE_LABELS[r] }))}
          value={value.route}
          onChange={(route: SiteSystemRoute) => onChange({ type: "system", route })}
        />
      ) : null}
      {value?.type === "legal" ? (
        <LegalTargetField ariaLabel={`${label}: документ`} value={value.id || null} invalid={!!error} onChange={(id) => onChange({ type: "legal", id })} />
      ) : list && value && "id" in value ? (
        targets.failed[list] ? (
          <p className="type-caption text-on-surface-variant">Не удалось загрузить список — выберите «Адрес или телефон» и впишите путь, например /about.</p>
        ) : (
          <Combobox
            options={(targets.lists[list] ?? []).map((t) => ({ value: t.id, label: t.label, description: t.description }))}
            value={value.id || null}
            placeholder={targets.lists[list] ? "Выберите…" : "Загружаем…"}
            invalid={!!error}
            onChange={(id) => onChange({ ...value, id: id ?? "" })}
          />
        )
      ) : null}
      {value?.type === "url" ? (
        <Input
          inputMode="url"
          placeholder="/about, https://…, tel:+7… или mailto:…"
          value={value.url}
          aria-invalid={urlInvalid || !!error}
          onChange={(e) => onChange({ type: "url", url: e.target.value.trim() })}
        />
      ) : null}
      {urlInvalid ? <p className="type-caption text-destructive">Адрес — путь сайта «/…», https://…, tel: или mailto:</p> : null}
      {error ? <p className="type-caption text-destructive">{error}</p> : null}
    </div>
  );
}
